import React, {useCallback, useEffect, useRef, useState} from "react";
import useTranslate from "../../modules/core/languages/hooks/useTranslate";
import InputField from "../../modules/ui/components/InputField/InputField";
import Label from "../../modules/ui/components/Label/Label";
import Select from "../../modules/ui/shared/Select/Select";
import {DIAL_CODES} from "../../modules/ui/data/dialCodes";
import Input from "../../modules/ui/shared/Input/Input";
import InputError from "../../modules/ui/shared/InputError/InputError";
import BookingNationalInputs from "../../modules/booking/components/BookingNationalInputs";
import ButtonPrimary from "../../modules/ui/shared/Button/ButtonPrimary";
import {SubmitHandler, useForm} from "react-hook-form";
import Profile from "../../modules/profile/types/Profile";
import {useOidcClientContext} from "../../modules/ui/context/OidcClientContext";
import useProfileGet from "../../modules/profile/hooks/useProfileGet";
import useProfileSave from "../../modules/profile/hooks/useProfileSave";
import {Alert} from "../../modules/ui/shared/Alert/Alert";
import SelectField from "../../modules/ui/components/SelectField/SelectField";
import useChangeLanguage from "../../modules/core/languages/hooks/useChangeLanguage";
import {LANG_CODE_MAP} from "../../modules/customers/constants";
// import moment from "moment/moment";
import {parseInt} from "lodash";
import useCustomerContext from "../../modules/customers/hooks/useCustomerContext";
import LegalCheckModal from "../../modules/common/components/LegalCheckModal";
import CreditCardPaycometForm from "../../modules/payment/components/CreditCardPaycometForm";
import SavedCardsList from "../../modules/payment/components/SavedCardsList";
import {ShieldCheckIcon} from "@heroicons/react/24/outline";

const ProfileView = () => {
    const {authenticated} = useOidcClientContext();
    const {data: profile} = useProfileGet();
    const {customerTranslation} = useCustomerContext();
    const t = useTranslate();
    const form = useForm<Profile>({mode: "onBlur"});
    const {register, handleSubmit, watch, setValue, control, setError, formState: {errors}} = form;
    const {save: profileSave} = useProfileSave();
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>("");
    const [alertType, setAlertType] = useState<"default" | "warning" | "info" | "success" | "error">("info");
    const [showCardAlert, setShowCardAlert] = useState(false);
    const [cardAlertType, setCardAlertType] = useState<"success" | "error">("success");
    const [cardAlertMessage, setCardAlertMessage] = useState("");
    const [showAddCardForm, setShowAddCardForm] = useState(false);
    const cardAlertRef = useRef<HTMLDivElement | null>(null);
    // const [creditCardExpirationMonth] = form.watch(['cardDateMonth']);
    const changeLang = useChangeLanguage();

    useEffect(() => {
        if (authenticated && profile) {
            setValue("firstName", profile?.firstName);
            setValue("lastName", profile?.lastName);
            setValue("email", profile?.email);
            setValue("phoneNumber", profile?.phoneNumber);
            setValue("cardName", profile?.cardName);
            setValue("cardNumberDecrypted", profile?.cardNumberDecrypted);
            setValue("invoiceFullName", profile?.invoiceFullName);
            setValue("invoiceAddress", profile?.invoiceAddress);
            setValue("language", profile?.language);
            setValue("terms", profile?.terms || false);
            if (profile?.cardExpiration) {
                const [month, year] = profile.cardExpiration.split('/');
                setValue("cardDateMonth", month);
                setValue("cardDateYear", year);
            }
        }
    }, [authenticated, profile]);

    useEffect(() => {
        if (showCardAlert) {
            cardAlertRef.current?.scrollIntoView({behavior: "smooth", block: "center"});
        }
    }, [showCardAlert, cardAlertMessage]);

    /*const isCardExpValid = (value?: string) => {
        if (!creditCardExpirationMonth && !value) {
            return true;
        }

        const expirationDate = moment({
            year: parseInt(`20${value}`),
            month: parseInt(creditCardExpirationMonth) - 1,
            day: 1,
            hour: 0,
            minute: 0,
            second: 0
        })

        if (moment(new Date(), 'DD/MM/YYYY HH:mm:ss').isAfter(expirationDate)) {
            return t("booking.invalidCreditCardExpirationActualDate")
        }

        return true;
    };*/

    const handleChangeLang = useCallback((lang?: string) => {
        if (!lang) {
            return;
        }

        const langId = lang.split('/').pop();
        changeLang(LANG_CODE_MAP[langId]);
    }, [changeLang]);

    const onSubmit: SubmitHandler<Profile> = async (data) => {
        data.phoneNumber = parseInt(data.phoneNumber + "");
        data.isoAndDialCode = data.dial.split('-').join('+');
        data.documentNumber = data.invoicePassportCif;
        data.cardExpiration = data.cardDateMonth && data.cardDateYear ? `${data.cardDateMonth}/${data.cardDateYear}` : undefined;

        profileSave(data, {
            onSuccess: () => {
                setAlertType("success");
                setAlertMessage(t("common.feedback.saved_data"));
                setShowAlert(true);
                handleChangeLang(data.language);

                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            },
            onError: (error: any) => {
                if (error.statusCode === 422) {
                    setAlertMessage(t("common.feedback.form_not_valid"));
                    error.content.violations.forEach((v => {
                        setError(v.propertyPath, {message: v.message});
                    }));
                } else {
                    setAlertMessage(t("common.feedback.unexpected_error"));
                }
                setAlertType("error");
                setShowAlert(true);
            }
        });
    };

    if (!profile) {
        return null;
    }

    return (
        <div
            className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-2 px-0 sm:p-6 xl:p-8">
            <h2 className="text-3xl lg:text-4xl font-semibold">
                {t("mainNav.profile")}
            </h2>
            {showAlert && (
                <Alert
                    type={alertType}
                    closable={true}
                    onClose={() => setShowAlert(false)}
                >
                    {alertMessage}
                </Alert>
            )}
            <div>
                <div className="mt-6">
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="border-b border-neutral-200 dark:border-neutral-700 my-5"></div>
                        <h3 className="text-2xl font-semibold mb-5">{t("booking.personalInformation")}</h3>
                        <div className="lg:flex lg:space-x-5">
                            <div className="lg:w-1/3 mb-2">
                                <InputField
                                    label={t("booking.name")}
                                    type="text"
                                    defaultValue={profile.firstName || ""}
                                    {...register("firstName", {required: t("common.feedback.field_required")})}
                                    error={errors.firstName?.message}
                                />
                            </div>
                            <div className="lg:w-1/3 mb-2">
                                <InputField
                                    label={t("booking.surname")}
                                    type="text"
                                    defaultValue={profile.lastName || ""}
                                    {...register("lastName", {required: t("common.feedback.field_required")})}
                                    error={errors.lastName?.message}
                                />
                            </div>
                            <div className="lg:w-1/3 mb-2">
                                <InputField
                                    label={t("booking.email")}
                                    type="email"
                                    defaultValue={profile.email || ""}
                                    {...register("email", {required: t("common.feedback.field_required")})}
                                    error={errors.email?.message}
                                />
                            </div>
                        </div>
                        <div className="lg:flex lg:space-x-5">
                            <div className="lg:w-1/2 mb-2">
                                <SelectField
                                    label={t("common.labels.language")}
                                    error={errors.language?.message}
                                    defaultValue={profile.language || ""}
                                    {...register("language", {required: t("common.feedback.field_required")})}
                                >
                                    <option value="">{t("")}</option>
                                    <option key={'/languages/1'} value={'/languages/1'}>
                                        {'Español'}
                                    </option>
                                    <option key={'/languages/2'} value={'/languages/2'}>
                                        {'English'}
                                    </option>
                                    <option key={'/languages/3'} value={'/languages/3'}>
                                        {'Français'}
                                    </option>
                                    <option key={'/languages/4'} value={'/languages/4'}>
                                        {'Italiano'}
                                    </option>
                                </SelectField>
                            </div>
                            <div className="lg:w-1/2 space-y-1 mb-2">
                                <Label
                                    isInvalid={!!errors.dialCode || !!errors.phoneNumber}>{t("booking.telephone")}</Label>
                                <div className="flex">
                                    <Select
                                        className="w-1/2 rounded-tr-none rounded-br-none"
                                        {...register("dial", {required: t("common.feedback.field_required")})}
                                        defaultValue="ES-34"
                                        isInvalid={!!errors.dialCode}
                                    >
                                        {DIAL_CODES.map(dialCode =>
                                            <option key={`${dialCode.code}-${dialCode.callingCode}`}
                                                    value={`${dialCode.code}-${dialCode.callingCode}`}>
                                                {dialCode.code} +{dialCode.callingCode}
                                            </option>
                                        )}
                                    </Select>
                                    <Input
                                        className="rounded-tl-none rounded-bl-none"
                                        type="text"
                                        defaultValue={profile.phoneNumber || ""}
                                        {...register("phoneNumber", {
                                            required: t("common.feedback.field_required"),
                                            pattern: {
                                                value: /^[0-9]{6,13}$/,
                                                message: t("booking.invalidTelephone")
                                            }
                                        })}
                                        isInvalid={!!errors.phoneNumber}
                                    />
                                </div>
                                {(errors.dialCode || errors.phoneNumber) &&
                                    <InputError>{errors.dialCode?.message || errors.phoneNumber?.message}</InputError>}
                            </div>
                        </div>
                        <div className="border-b border-neutral-200 dark:border-neutral-700 my-5"></div>
                        <h3 className="text-2xl font-semibold mb-5">{t("booking.invoiceInformation")}</h3>
                        <div className="lg:flex lg:space-x-5">
                            <div className="lg:w-1/2 mb-2">
                                <InputField
                                    label={t("booking.invoiceNameAndSurname")}
                                    defaultValue={profile?.invoiceFullName || ""}
                                    {...register("invoiceFullName", {required: t("common.feedback.field_required")})}
                                    error={errors.invoiceFullName?.message}
                                />
                            </div>
                            <div className="lg:w-1/2 mb-2">
                                <InputField
                                    label={t("booking.invoiceAddress")}
                                    defaultValue={profile?.invoiceAddress || ""}
                                    {...register("invoiceAddress", {required: t("common.feedback.field_required")})}
                                    error={errors.invoiceAddress?.message}
                                />
                            </div>
                        </div>
                        <BookingNationalInputs
                            t={t}
                            errors={errors}
                            register={register}
                            watch={watch}
                            setValue={setValue}
                            profile={profile}
                            control={control}
                        />

                        <div className="border-b border-neutral-200 dark:border-neutral-700 my-5"></div>
                        <h3 className="text-2xl font-semibold mb-5">{t("booking.paymentInformation")}</h3>

                        {/* ── Alerta resultado tarjeta ── */}
                        {showCardAlert && (
                            <div ref={cardAlertRef}>
                                <Alert
                                    type={cardAlertType}
                                    closable
                                    onClose={() => setShowCardAlert(false)}
                                    containerClassName="mb-4"
                                >
                                    {cardAlertMessage}
                                </Alert>
                            </div>
                        )}

                        {/* ── Lista de tarjetas guardadas ── */}
                        <SavedCardsList
                            onAddCard={() => setShowAddCardForm(true)}
                            onDeleteError={(errorMsg) => {
                                setCardAlertType("error");
                                setCardAlertMessage(errorMsg);
                                setShowCardAlert(true);
                            }}
                        />

                        {/* ── Formulario añadir tarjeta (expandible) ── */}
                        {showAddCardForm && (
                            <div className="mt-4 rounded-3xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                                <div className="px-6 py-6">
                                    <CreditCardPaycometForm
                                        submitLabel={t("paymentCard.saveCard")}
                                        onSuccess={() => {
                                            setCardAlertType("success");
                                            setCardAlertMessage(t("paymentCard.cardSavedSuccess"));
                                            setShowCardAlert(true);
                                            setShowAddCardForm(false);
                                        }}
                                        onError={() => {
                                            setCardAlertType("error");
                                            setCardAlertMessage(t("common.feedback.unexpected_error"));
                                            setShowCardAlert(true);
                                        }}
                                    />
                                </div>
                                <div className="flex items-center justify-between px-6 py-3 bg-neutral-50 dark:bg-neutral-800 border-t border-neutral-200 dark:border-neutral-700">
                                    <div className="flex items-center gap-2">
                                        <ShieldCheckIcon className="h-4 w-4 text-green-500 flex-shrink-0"/>
                                        <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                            {t("paymentCard.securePayment")}
                                        </span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setShowAddCardForm(false)}
                                        className="text-xs text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300"
                                    >
                                        {t("common.actions.cancel")}
                                    </button>
                                </div>
                            </div>
                        )}
                        {customerTranslation?.privacyPolicy && (
                            <LegalCheckModal
                                name="terms"
                                register={register}
                                watch={watch}
                                setValue={setValue}
                                errors={errors}
                                contentKey={customerTranslation?.privacyPolicy}
                                legalLinkKey="footer.privacyPolicy"
                            />
                        )}
                        <div className="pt-4">
                            <ButtonPrimary
                                loading={profileSave.isLoading}
                                type="submit">{t("common.actions.save")}
                            </ButtonPrimary>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default ProfileView;
